/**
 * STR (Section-Township-Range) pricing data.
 *
 * When Supabase is configured, pricing is loaded from the shared `str_pricing`
 * table so all users see the same map.  The static object below serves as a
 * fallback for offline / localStorage-only mode.
 *
 * Keys are normalized as "Section-TownshipDirection-RangeDirection"
 * e.g. "10-25S-25E". Entries with a tract qualifier (E2, W2) use
 * a colon suffix: "17-24S-34E:W2".
 *
 * Values are dollar amounts.
 */

const STATIC_PRICING = {
  '10-25S-25E':    12000,
  '9-25S-25E':     12000,
  '18-22S-27E':    18000,
  '17-22S-27E':    18000,
  '21-25S-27E':    49700,
  '2-23S-26E':     10000,
  '1-23S-26E':     9900,
  '3-23S-26E':     10000,
  '3-25S-26E':     26500,
  '2-25S-26E':     34000,
  '4-25S-26E':     12000,
  '3-26S-25E':     12700,
  '10-26S-25E':    12700,
  '8-20S-27E':     8000,
  '7-20S-27E':     8000,
  '1-21S-31E':     43000,
  '26-20S-32E':    51000,
  '35-20S-32E':    51000,
  '2-21S-31E':     33000,
  '1-24S-32E':     19500,
  '17-24S-34E:W2': 16000,
  '17-24S-34E:E2': 12800,
  '19-18S-34E':    19000,
  '18-18S-34E':    19000,
  '27-22S-35E':    4000,
  '28-26S-27E':    13000,
  '21-26S-27E':    29000,
  '29-18S-35E':    33000,
  '30-22S-33E':    23000,
  '27-22S-32E':    17000,
  '31-22S-33E':    23000,
  '34-22S-32E':    17000,
  '34-25S-32E:E2': 37500,
  '34-25S-32E:W2': 50000,
  '10-26S-32E':    19000,
  '36-20S-32E':    25000,
  '6-21S-32E':     26500,
  '16-25S-27E':    49700,
  '22-22S-35E':    4000,
};

// ── Live pricing map (populated from Supabase or static fallback) ────────────

let STR_PRICING = { ...STATIC_PRICING };

/**
 * Replace the in-memory pricing map with data from the database.
 * Called by AppContext after fetching from Supabase.
 *
 * @param {Array<{id:string, section:string, township:string, range:string, tract:string|null, price:number}>} rows
 */
export function loadPricingFromDB(rows) {
  if (!rows || rows.length === 0) return;
  const map = {};
  for (const r of rows) {
    map[r.id] = r.price;
    if (r.company) STR_PRICING_COMPANIES[r.id] = r.company;
  }
  STR_PRICING = map;
}

/**
 * Look up pricing for a given Section-Township-Range.
 *
 * @param {string|number} section  - e.g. "10" or 10
 * @param {string} township        - e.g. "25S"
 * @param {string} range           - e.g. "25E"
 * @returns {{ price: number, label: string } | null}
 *   Returns the price and a formatted label like "$12,000", or null if not found.
 *   If multiple tract entries exist (E2/W2), returns an array of { tract, price, label }.
 */
export function getSTRPricing(section, township, range) {
  const sec = String(section).trim();
  const twp = (township || '').trim().toUpperCase();
  const rng = (range || '').trim().toUpperCase();
  const key = `${sec}-${twp}-${rng}`;

  // Check for exact match first
  if (STR_PRICING[key] != null) {
    return [{ tract: null, price: STR_PRICING[key], label: formatPrice(STR_PRICING[key]), company: STR_PRICING_COMPANIES[key] || '' }];
  }

  // Check for tract-qualified entries (E2, W2, etc.)
  const tractEntries = Object.entries(STR_PRICING)
    .filter(([k]) => k.startsWith(key + ':'))
    .map(([k, price]) => ({
      tract: k.split(':')[1],
      price,
      label: formatPrice(price),
      company: STR_PRICING_COMPANIES[k] || '',
    }));

  return tractEntries.length > 0 ? tractEntries : null;
}

/**
 * Update (or add) pricing for a given Section-Township-Range.
 * Updates the in-memory map immediately; caller should also persist via store.
 *
 * @param {string|number} section
 * @param {string} township  e.g. "25S"
 * @param {string} range     e.g. "25E"
 * @param {number} price     dollar amount
 * @param {string} [tract]   optional tract qualifier e.g. "E2"
 */
export function updateSTRPricing(section, township, range, price, tract, company) {
  const sec = String(section).trim();
  const twp = (township || '').trim().toUpperCase();
  const rng = (range || '').trim().toUpperCase();
  const key = tract
    ? `${sec}-${twp}-${rng}:${tract}`
    : `${sec}-${twp}-${rng}`;
  STR_PRICING[key] = price;
  STR_PRICING_DATES[key] = new Date().toISOString();
  if (company !== undefined) STR_PRICING_COMPANIES[key] = company || '';
}

const STR_PRICING_DATES = {};
const STR_PRICING_COMPANIES = {};

export function deleteSTRPricingLocal(section, township, range) {
  const key = `${String(section).trim()}-${(township || '').trim().toUpperCase()}-${(range || '').trim().toUpperCase()}`;
  delete STR_PRICING[key];
  delete STR_PRICING_DATES[key];
  delete STR_PRICING_COMPANIES[key];
}

export function getPricingDate(section, township, range) {
  const key = `${String(section).trim()}-${(township || '').trim().toUpperCase()}-${(range || '').trim().toUpperCase()}`;
  return STR_PRICING_DATES[key] || null;
}

export function getPricingCompany(section, township, range) {
  const key = `${String(section).trim()}-${(township || '').trim().toUpperCase()}-${(range || '').trim().toUpperCase()}`;
  return STR_PRICING_COMPANIES[key] || '';
}

function formatPrice(amount) {
  return '$' + amount.toLocaleString('en-US');
}

/**
 * Return all section-level pricing entries grouped by township.
 * Returns: { "23S-28E": { township, range, sections: [{ section, price }] } }
 */
export function getSectionPricingByTownship() {
  const groups = {};
  for (const [key, price] of Object.entries(STR_PRICING)) {
    if (typeof price !== 'number' || price <= 0) continue;
    const base = key.split(':')[0];
    const parts = base.split('-');
    if (parts.length < 3) continue;
    const sec = parts[0], twp = parts[1], rng = parts[2];
    const twpKey = `${twp}-${rng}`;
    if (!groups[twpKey]) groups[twpKey] = { township: twp, range: rng, sections: [] };
    const company = STR_PRICING_COMPANIES[key] || '';
    // Average duplicates for same section (e.g. different tracts)
    const existing = groups[twpKey].sections.find(s => s.section === sec);
    if (existing) {
      existing.prices.push(price);
      existing.price = Math.round(existing.prices.reduce((s, p) => s + p, 0) / existing.prices.length);
      if (company && !existing.company) existing.company = company;
    } else {
      groups[twpKey].sections.push({ section: sec, price, prices: [price], company });
    }
  }
  return groups;
}

export default STR_PRICING;
